/* =========================================================================
   AROMATEX · Brand Scent Advisor
   engine.js — Lógica de scoring, hard rules y construcción del resultado
   ========================================================================= */

/**
 * calculateRecommendations(answers)
 * -------------------------------------------------------------------------
 * 1. Inicializa todos los aromas del catálogo en 0.
 * 2. Suma puntos por: industria, espacio, emociones, personalidad y objetivo.
 * 3. Aplica reglas de intensidad según tiempo de permanencia + intensidad deseada.
 * 4. Aplica hard rules (penalize / force_include).
 * 5. Ordena los aromas por score descendente.
 * 6. Devuelve { top5, ranked, flags } donde `ranked` es la lista completa
 *    (útil para "también te recomendamos" y para el payload de CRM).
 *
 * @param {Object} answers - respuestas crudas del quiz (ver app.js -> ANSWERS)
 * @returns {Object} { top5, ranked, flags, breakdown }
 */
function calculateRecommendations(answers) {
  // ---- 1. Inicializar scores en 0 ----------------------------------------
  const scores = {};
  const breakdown = {}; // trazabilidad: por qué cada aroma obtuvo su score
  Object.keys(AROMA_CATALOG).forEach((aroma) => {
    scores[aroma] = 0;
    breakdown[aroma] = [];
  });

  const addPoints = (aroma, points, label) => {
    if (!(aroma in scores)) return; // ignora llaves que no existan en el catálogo
    scores[aroma] += points;
    breakdown[aroma].push({ label, points });
  };

  const addTable = (table, label, weight = 1) => {
    if (!table) return;
    Object.entries(table).forEach(([aroma, points]) => {
      addPoints(aroma, Math.round(points * weight), label);
    });
  };

  // ---- 2a. Industria -------------------------------------------------------
  const industryKey = MAPPINGS.industry[answers.industry] || null;
  if (industryKey && SCORING.industry[industryKey]) {
    addTable(SCORING.industry[industryKey], `Industria: ${answers.industry}`);
  }

  // ---- 2b. Espacios (peso reducido, pueden ser varios) ---------------------
  (answers.spaces || []).forEach((space) => {
    const key = MAPPINGS.spaceToIndustryKey[space];
    if (key && SCORING.industry[key]) {
      addTable(SCORING.industry[key], `Espacio: ${space}`, MAPPINGS.spaceWeight);
    }
  });

  // ---- 2c. Emociones (hasta 3) ----------------------------------------------
  (answers.feelings || []).forEach((feeling) => {
    const key = MAPPINGS.emotion[feeling];
    if (key && SCORING.emotions[key]) {
      addTable(SCORING.emotions[key], `Emoción: ${feeling}`);
    }
  });

  // ---- 2d. Personalidad de marca (hasta 5) -----------------------------------
  (answers.personality || []).forEach((trait) => {
    const key = MAPPINGS.personality[trait];
    if (key && SCORING.brand_personality[key]) {
      addTable(SCORING.brand_personality[key], `Personalidad: ${trait}`);
    }
  });

  // ---- 2e. Objetivo principal -------------------------------------------------
  const goalKey = MAPPINGS.mainGoal[answers.mainGoal] || null;
  if (goalKey && SCORING.main_goal[goalKey]) {
    addTable(SCORING.main_goal[goalKey], `Objetivo: ${answers.mainGoal}`);
  }

  // ---- 3. Reglas de intensidad (tiempo de permanencia + intensidad deseada) --
  const stayKey = MAPPINGS.stayTime[answers.stayTime] || null;
  const stayRule = stayKey ? SCORING.stay_time[stayKey] : null;
  const intensityKey = MAPPINGS.desiredIntensity[answers.desiredIntensity] || null;
  const intensityRule = intensityKey ? SCORING.desired_intensity[intensityKey] : null;

  Object.entries(AROMA_CATALOG).forEach(([aroma, info]) => {
    const level = info.intensity; // "Suave" | "Media" | "Alta"

    if (stayRule) {
      if (Array.isArray(stayRule.allowIntensity) && !stayRule.allowIntensity.includes(level)) {
        // no es hard-exclusión, pero si hay penalty explícita para Alta se aplica abajo
      }
      const bonusKey = `bonus${level}`;
      if (stayRule[bonusKey]) addPoints(aroma, stayRule[bonusKey], `Permanencia (${answers.stayTime}): bono ${level}`);
      if (level === "Alta" && stayRule.penaltyAlta) addPoints(aroma, stayRule.penaltyAlta, `Permanencia (${answers.stayTime}): penalización Alta`);
    }

    if (intensityRule) {
      const bonusKey = `bonus${level}`;
      if (intensityRule[bonusKey]) addPoints(aroma, intensityRule[bonusKey], `Intensidad deseada (${answers.desiredIntensity}): bono ${level}`);
      if (level === "Alta" && intensityRule.penaltyAlta) addPoints(aroma, intensityRule.penaltyAlta, `Intensidad deseada (${answers.desiredIntensity}): penalización Alta`);
    }
  });

  // ---- 4. Hard rules ----------------------------------------------------------
  const flags = { customFragrance: false, customFragranceMessage: null, forcedTop: null };
  const effectiveIndustryList = [industryKey, ...(answers.spaces || []).map((s) => MAPPINGS.spaceToIndustryKey[s])].filter(Boolean);

  HARD_RULES.forEach((rule) => {
    let matches = false;

    if (rule.condition.industry) {
      matches = rule.condition.industry.some(
        (ind) => ind === answers.industry || effectiveIndustryList.includes(ind)
      );
    }
    if (rule.condition.branches) {
      matches = matches || rule.condition.branches.includes(answers.branches);
    }
    if (!matches) return;

    if (rule.action === "penalize") {
      rule.aromas.forEach((aroma) => addPoints(aroma, rule.points, `Hard rule: ${rule.reason}`));
    }
    if (rule.action === "force_include") {
      rule.aromas.forEach((aroma) => {
        addPoints(aroma, 1000, `Hard rule (force_include): ${rule.reason}`);
        flags.forcedTop = aroma;
      });
    }
    if (rule.action === "show_custom_fragrance_message") {
      flags.customFragrance = true;
      flags.customFragranceMessage = rule.message;
    }
  });

  // ---- 5. Ordenar por score descendente ----------------------------------------
  const ranked = Object.entries(scores)
    .map(([aroma, score]) => ({
      aroma,
      score,
      info: AROMA_CATALOG[aroma],
      why: breakdown[aroma],
    }))
    .sort((a, b) => b.score - a.score);

  // ---- 6. Top 5 -----------------------------------------------------------------
  const top5 = ranked.slice(0, 5);
  const alsoRecommended = ranked.slice(5, 8);

  return { top5, alsoRecommended, ranked, flags, scores };
}

/**
 * determineArchetype(answers)
 * Compara las respuestas del usuario contra los tags de cada arquetipo
 * y devuelve el de mayor coincidencia.
 */
function determineArchetype(answers) {
  const userTags = [
    answers.industry,
    answers.mainGoal,
    ...(answers.feelings || []),
    ...(answers.personality || []),
  ].filter(Boolean);

  let best = { key: "Luxury Signature", score: -1 };
  Object.entries(ARCHETYPES).forEach(([key, tags]) => {
    const score = tags.reduce((acc, tag) => acc + (userTags.includes(tag) ? 1 : 0), 0);
    if (score > best.score) best = { key, score };
  });
  return { key: best.key, label: ARCHETYPE_LABELS[best.key] || best.key };
}

/**
 * compatibilityScore(top5)
 * Convierte el score bruto del aroma #1 en un porcentaje "de marketing"
 * entre 70% y 99%, siempre alto pero creíble.
 */
function compatibilityScore(ranked) {
  const top = ranked[0] ? ranked[0].score : 0;
  const MAX_EXPECTED = 160; // score aproximado de un match "perfecto"
  const pct = 70 + (Math.max(0, top) / MAX_EXPECTED) * 29;
  return Math.max(70, Math.min(99, Math.round(pct)));
}

/**
 * recommendedIntensityLabel(top5)
 * Determina la intensidad recomendada dominante entre los aromas top 5.
 */
function recommendedIntensityLabel(top5) {
  const counts = { Suave: 0, Media: 0, Alta: 0 };
  top5.forEach((r) => counts[r.info.intensity]++);
  return Object.entries(counts).sort((a, b) => b[1] - a[1])[0][0];
}

/**
 * reasoningFor(aroma, info)
 * Genera un texto de "por qué se recomienda" a partir de los datos
 * propios del catálogo (emociones + adjetivos + tipo de espacio).
 */
function reasoningFor(info) {
  const emo = info.emotions.slice(0, 2).join(" y ").toLowerCase();
  const adj = info.adjectives.slice(0, 2).join(" y ").toLowerCase();
  return `Por su capacidad de transmitir ${emo}, con un carácter ${adj}, es ideal para ${info.spaceType}.`;
}

/**
 * buildResultPayload(answers)
 * Arma el objeto final: respuestas + scores completos + top5 + metadata,
 * listo para enviar a Klaviyo / Zoho / cualquier CRM vía webhook.
 */
function buildResultPayload(answers) {
  const { top5, alsoRecommended, ranked, flags, scores } = calculateRecommendations(answers);
  const archetype = determineArchetype(answers);
  const compatibility = compatibilityScore(ranked);
  const intensity = recommendedIntensityLabel(top5);

  return {
    meta: {
      generated_at: new Date().toISOString(),
      source: "Aromatex Brand Scent Advisor",
      version: "1.0.0",
    },
    lead: {
      name: answers.lead?.name || "",
      company: answers.lead?.company || answers.companyName || "",
      email: answers.lead?.email || "",
      whatsapp: answers.lead?.whatsapp || "",
    },
    answers,
    result: {
      title: RESULT_FORMAT.result_title,
      brand_archetype: archetype,
      compatibility_percent: compatibility,
      recommended_intensity: intensity,
      top_5_aromas: top5.map((r, i) => ({
        rank: i + 1,
        name: r.aroma,
        family: r.info.family,
        score: r.score,
        intensity: r.info.intensity,
        reasoning: reasoningFor(r.info),
      })),
      also_recommended: alsoRecommended.map((r) => ({ name: r.aroma, family: r.info.family, score: r.score })),
      custom_fragrance_flag: flags.customFragrance,
      custom_fragrance_message: flags.customFragranceMessage,
    },
    scores_full: scores,
  };
}
